<script lang="ts">
  import { store } from '../lib/store.svelte.ts';
  import { router } from '../lib/router.svelte.ts';
  import { roundBadge } from '../lib/format.ts';

  /**
   * Computed locally from the log, so the reports work offline and can never disagree with
   * what the phone shows — the office and the field run the same projection.
   */
  let tab = $state<'missing' | 'stale' | 'rounds'>('missing');
  let clockSuspect = $state<{ round_id: string; site_code: string; worst_skew_minutes: number; opened_at: string }[] | null>(null);
  let clockError = $state<string | null>(null);

  const totals = $derived.by(() => {
    const all = [...store.states.values()];
    return {
      carts: all.length,
      active: all.filter((s) => s.status === 'active').length,
      unplaced: all.filter((s) => s.status === 'active' && !s.current_site).length,
      stale: store.staleCarts.length,
      missing: store.missingCarts.length,
    };
  });

  async function loadClockSuspect() {
    clockError = null;
    try {
      const res = await fetch('/api/reports/clock-suspect', { credentials: 'include' });
      if (!res.ok) throw new Error(res.status === 403 ? 'Réservé aux administrateurs' : `HTTP ${res.status}`);
      clockSuspect = await res.json();
    } catch (e) {
      clockError = e instanceof Error ? e.message : String(e);
    }
  }
</script>

<h1>Rapports</h1>

<div class="stat-row">
  <div class="stat"><div class="k">en service</div><div class="n">{totals.active}</div></div>
  <div class="stat" class:is-danger={totals.missing > 0}><div class="k">manquants</div><div class="n">{totals.missing}</div></div>
  <div class="stat" class:is-warn={totals.stale > 0}><div class="k">non vus &gt; {store.staleAfterDays} j</div><div class="n">{totals.stale}</div></div>
  <div class="stat"><div class="k">jamais localisés</div><div class="n">{totals.unplaced}</div></div>
</div>

<div class="segmented no-print" role="group" aria-label="Type de rapport">
  <button aria-pressed={tab === 'missing'} onclick={() => (tab = 'missing')}>Manquants</button>
  <button aria-pressed={tab === 'stale'} onclick={() => (tab = 'stale')}>Anciens</button>
  <button aria-pressed={tab === 'rounds'} onclick={() => (tab = 'rounds')}>Tournées</button>
</div>

{#if tab === 'missing'}
  <div class="card">
    <h2>Signalés manquants ({store.missingCarts.length})</h2>
    <p class="tiny muted">
      Attendus sur un site lors d'une clôture de tournée et non trouvés. Un scan ultérieur,
      n'importe où, retire automatiquement le signalement.
    </p>
    {#if store.missingCarts.length === 0}
      <p class="empty">Aucun.</p>
    {:else}
      <ul class="list">
        {#each store.missingCarts as s (s.code)}
          <li>
            <button class="row-link" onclick={() => router.go(`carts/${s.code}`)}>
              <span class="stack" style="gap:.1rem;align-items:flex-start">
                <span class="truncate row-title">{s.name}</span>
                <span class="tiny muted"><span class="mono">{s.code}</span> · dernière position {store.siteName(s.current_site)}</span>
              </span>
            </button>
            <span class="pill danger">{new Date(s.missing_since!).toLocaleDateString('fr-FR')}</span>
            {#if s.missed_count > 1}<span class="pill">×{s.missed_count}</span>{/if}
          </li>
        {/each}
      </ul>
    {/if}
  </div>
{:else if tab === 'stale'}
  <div class="card">
    <h2>Non confirmés depuis plus de {store.staleAfterDays} jours ({store.staleCarts.length})</h2>
    <p class="tiny muted">
      Aucune tournée ne les a vus récemment, où que ce soit. Un chariot ici n'est pas
      forcément perdu — il peut simplement être sur un site qu'on ne relève pas souvent.
    </p>
    {#if store.staleCarts.length === 0}
      <p class="empty">Aucun.</p>
    {:else}
      <ul class="list">
        {#each store.staleCarts as s (s.code)}
          <li>
            <button class="row-link" onclick={() => router.go(`carts/${s.code}`)}>
              <span class="stack" style="gap:.1rem;align-items:flex-start">
                <span class="truncate row-title">{s.name}</span>
                <span class="mono tiny muted">{s.code}</span>
              </span>
            </button>
            <span class="tiny muted right">
              {s.last_seen_at ? new Date(s.last_seen_at).toLocaleDateString('fr-FR') : 'jamais'}
            </span>
          </li>
        {/each}
      </ul>
    {/if}
  </div>
{:else}
  <div class="card">
    <h2>Tournées ({store.rounds.length})</h2>
    {#if store.rounds.length === 0}
      <p class="empty">Aucune tournée enregistrée.</p>
    {:else}
      <ul class="list">
        {#each store.rounds.slice(0, 100) as r (r.round_id)}
          {@const b = roundBadge(r)}
          <li>
            <button class="row-link" onclick={() => router.go(`rounds/${r.round_id}`)}>
              <span class="stack" style="gap:.1rem;align-items:flex-start">
                <span class="row-title truncate">{store.siteName(r.site_code)}</span>
                <span class="tiny muted">{new Date(r.opened_at).toLocaleString('fr-FR')}</span>
              </span>
            </button>
            <span class="pill">{b.count}</span>
            <span class="pill {b.tone}">{b.status}</span>
          </li>
        {/each}
      </ul>
    {/if}
  </div>
{/if}

{#if store.stalled.length > 0}
  <div class="card">
    <h2>Tournées restées ouvertes ({store.stalled.length})</h2>
    <p class="tiny muted">
      Ouvertes puis jamais clôturées ni abandonnées. Elles ne signalent aucun manquant et ne
      comptent plus comme tournée en cours — mais elles indiquent un relevé laissé en plan.
    </p>
    <ul class="list">
      {#each store.stalled as r (r.round_id)}
        <li>
          <button class="row-link" onclick={() => router.go(`rounds/${r.round_id}`)}>
            <span class="stack" style="gap:.1rem;align-items:flex-start">
              <span class="row-title truncate">{store.siteName(r.site_code)}</span>
              <span class="tiny muted">{new Date(r.opened_at).toLocaleString('fr-FR')}</span>
            </span>
          </button>
          <span class="pill">{r.scanned.length} scannés</span>
          <span class="pill warn">en plan</span>
        </li>
      {/each}
    </ul>
  </div>
{/if}

<div class="card no-print">
  <h2>Horloges suspectes</h2>
  <p class="tiny muted">
    L'ordre des événements suit l'horloge de l'appareil, pour que téléphone et serveur
    calculent la même chose. Une horloge fausse est donc signalée ici plutôt que corrigée en
    silence.
  </p>
  <button class="slim" onclick={loadClockSuspect}>Vérifier (nécessite une connexion)</button>
  {#if clockError}<div class="banner danger" style="margin-top:.5rem">{clockError}</div>{/if}
  {#if clockSuspect}
    {#if clockSuspect.length === 0}
      <div class="banner ok" style="margin-top:.5rem">Aucun écart d'horloge significatif.</div>
    {:else}
      <ul class="list">
        {#each clockSuspect as r (r.round_id)}
          <li>
            <span class="grow truncate">{store.siteName(r.site_code)}</span>
            <span class="tiny muted">{new Date(r.opened_at).toLocaleString('fr-FR')}</span>
            <span class="pill warn">{r.worst_skew_minutes} min</span>
          </li>
        {/each}
      </ul>
    {/if}
  {/if}
</div>

<div class="card no-print row wrap tiny">
  <span class="grow muted">Exports</span>
  <a href="/api/export/carts.csv">Chariots</a>
  <a href="/api/export/sites.csv">Sites</a>
  <a href="/api/export/rounds.csv">Tournées</a>
  <a href="/api/export/events.csv">Historique</a>
</div>
